/* eslint-disable */
import { Injectable } from '@angular/core';
import { Observable, Observer } from 'rxjs';
import { MultiselectOptionItem, UiConstants } from '../util/core-utils';
import { MemStorage } from './util/mem-storage';
import { Order, OrderType } from './util/services';
import { UserGroup } from './user-group.service';
import { DisabledItem } from '../util/search-utils';
import { LoggedInUserStorage } from './util/storages';

/* eslint-enable */

@Injectable()
export class UserGroupSearchService {

  constructor() {
  }

  getSearchData(request: UserGroupSearch.SearchDataGetRequest): Observable<UserGroupSearch.SearchDataResult> {
    const storage = this.createStorage();
    return Observable.create((observer: Observer<UserGroupSearch.SearchDataResult>) => {
      try {
        observer.next(storage.read(request));
      }
      catch (e) {
        observer.error(e);
      }
      observer.complete();
      return function () {
      };
    });
  }

  setSearchData(request: UserGroupSearch.SearchDataSetRequest): Observable<UserGroupSearch.SearchDataResult> {
    const storage = this.createStorage();
    return Observable.create((observer: Observer<UserGroupSearch.SearchDataResult>) => {
      try {
        observer.next(storage.save(request));
      }
      catch (e) {
        observer.error(e);
      }
      observer.complete();
      return function () {
      };
    });
  }

  resetSearchData(request: UserGroupSearch.SearchDataGetRequest): Observable<UserGroupSearch.SearchDataResult> {
    const storage = this.createStorage();
    return Observable.create((observer: Observer<UserGroupSearch.SearchDataResult>) => {
      try {
        observer.next(storage.reset(request));
      }
      catch (e) {
        observer.error(e);
      }
      observer.complete();
      return function () {
      };
    });
  }

  private createStorage(): UserGroupSearch.Storage {
    return new UserGroupSearch.Storage();
  }

}

export namespace UserGroupSearch {

  export interface SearchDataGetRequest {
  }

  export interface SearchDataSetRequest {
    searchData: SearchData;
  }

  export interface SearchDataResult {
    searchData: SearchData;
  }

  export interface SearchData {
    itemsPerPage: number;
    pageNumber: number;
    order: Order<UserGroup.OrderField>;
    id: string;
    name: string;
    disabled?: DisabledItem;
    adminGroup?: boolean;
    applicationTypeIds: string;
    companies: MultiselectOptionItem<number>[];
    roles: MultiselectOptionItem<number>[];
  }

  interface StoredSearchRawDataBase {
    version: number;
  }

  interface StoredSearchRawData extends StoredSearchRawDataBase {
    itemsPerPage: number;
    pageNumber: number;
    order: Order<UserGroup.OrderField>;
    id: string;
    name: string;
    disabled?: DisabledItem;
    adminGroup?: boolean;
    applicationTypeIds: string;
    companies: MultiselectOptionItem<number>[];
    roles: MultiselectOptionItem<number>[];
  }

  export class Storage {

    /**
     * Increment this version if you change the SearchData interface.
     */
    private static readonly VERSION: number = 5;

    private static readonly DEFAULT_SEARCH_DATA: SearchData = {
      itemsPerPage: UiConstants.pageNumbers[0],
      pageNumber: 1,
      order: {
        field: UserGroup.OrderField.ID,
        type: OrderType.DESC
      },
      id: '',
      name: '',
      disabled: DisabledItem.active(),
      adminGroup: undefined,
      applicationTypeIds: '',
      companies: [],
      roles: [],
    };

    constructor() {
    }

    public read(request: SearchDataGetRequest): SearchDataResult {
      let rawBase: StoredSearchRawDataBase;
      try {
        const key = this.generateKey();
        const json = MemStorage.getItem(key);
        if (!json) {
          return this.createDefaultResult();
        }
        rawBase = JSON.parse(json);
      }
      catch (error) {
        return this.createDefaultResult();
      }
      if (Storage.VERSION === rawBase.version) {
        const raw: StoredSearchRawData = <StoredSearchRawData>rawBase;
        return this.fromRaw(raw);
      }
      return this.createDefaultResult();
    }

    public reset(request: SearchDataGetRequest): SearchDataResult {
      return this.save({
        searchData: Storage.DEFAULT_SEARCH_DATA,
      });
    }

    public save(request: SearchDataSetRequest): SearchDataResult {
      const key = this.generateKey();
      const raw = this.toRaw(request.searchData);
      const json = JSON.stringify(raw);
      MemStorage.setItem(key, json);
      return this.read(request);
    }

    private generateKey(): string {
      return 'UserId(' + LoggedInUserStorage.getInstance().getUserId() + ')-Search-User-Group';
    }

    private createDefaultResult(): SearchDataResult {
      return {
        searchData: Storage.DEFAULT_SEARCH_DATA,
      };
    }

    private fromRaw(data: StoredSearchRawData): SearchDataResult {
      return {
        searchData: {
          itemsPerPage: data.itemsPerPage,
          pageNumber: data.pageNumber,
          order: data.order,
          id: data.id,
          name: data.name,
          disabled: data.disabled,
          adminGroup: data.adminGroup,
          applicationTypeIds: data.applicationTypeIds,
          companies: data.companies,
          roles: data.roles,
        }
      };
    }

    private toRaw(data: SearchData): StoredSearchRawData {
      return {
        version: Storage.VERSION,
        itemsPerPage: data.itemsPerPage,
        pageNumber: data.pageNumber,
        order: data.order,
        id: data.id,
        name: data.name,
        disabled: data.disabled,
        adminGroup: data.adminGroup,
        applicationTypeIds: data.applicationTypeIds,
        companies: data.companies,
        roles: data.roles,
      };
    }

  }

}
